import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Servidor from "../config/Servidor";
import "./ConfigurarUsuarios.css";

export default function ConfigurarUsuarios() {

  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {

    try {

      setCargando(true);
      setError("");

      const res = await axios.get(
        `${Servidor.IP}/configurar/usuarios`
      );

      if (!res.data?.ok) {

        setError(
          res.data?.msg ||
          "No se pudieron obtener los usuarios"
        );

        return;
      }

      const lista =
        Array.isArray(res.data.usuarios)
          ? res.data.usuarios
          : [];

      setUsuarios(lista);

    } catch (err) {

      console.error(
        "Error cargando usuarios:",
        err
      );

      setError(
        err.response?.data?.msg ||
        "Error al conectar con el servidor"
      );

    } finally {

      setCargando(false);

    }
  };


  // --------------------------------------------------
  // Nombre completo
  // --------------------------------------------------

  const nombreCompleto = (usuario) => {

    return [
      usuario.nombre,
      usuario.primerapellido,
      usuario.segundoapellido
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  };


  // --------------------------------------------------
  // Normalizar texto para el buscador
  // --------------------------------------------------

  const normalizar = (valor) => {

    return String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  };


  // --------------------------------------------------
  // Texto completo que se puede buscar
  // --------------------------------------------------

  const textoBusqueda = (usuario) => {

    return normalizar([
      usuario.usuario,
      usuario.nombre,
      usuario.primerapellido,
      usuario.segundoapellido,
      usuario.cedula,
      usuario.celular,
      usuario.rol_nombre,
      usuario.id_usuario,
      usuario.id_persona,
      usuario.activo === 1
        ? "activo"
        : "inactivo"
    ].join(" "));

  };


  // --------------------------------------------------
  // Filtrado
  // --------------------------------------------------

  const usuariosFiltrados = useMemo(() => {

    const termino =
      normalizar(busqueda);

    let resultado = [...usuarios];

    if (termino) {

      resultado =
        resultado.filter(usuario =>
          textoBusqueda(usuario)
            .includes(termino)
        );

    }

    resultado.sort((a, b) => {

      const nombreA =
        normalizar(nombreCompleto(a));

      const nombreB =
        normalizar(nombreCompleto(b));

      return nombreA.localeCompare(
        nombreB,
        "es",
        {
          sensitivity: "base"
        }
      );

    });

    return resultado;

  }, [usuarios, busqueda]);


  // --------------------------------------------------
  // Abrir usuario
  // --------------------------------------------------

  const abrirUsuario = (usuario) => {

    navigate(
      `/configurarsupervisor/${usuario.id_usuario}`
    );

  };


  // --------------------------------------------------
  // Rol
  // --------------------------------------------------

  const obtenerRol = (usuario) => {

    if (usuario.rol_nombre) {
      return usuario.rol_nombre;
    }

    switch (Number(usuario.id_rol)) {

      case 1:
        return "Administrador";

      case 2:
        return "Supervisor";

      case 4:
        return "Independiente";

      default:
        return "Usuario";

    }

  };


  if (cargando) {

    return (
      <div className="configurar-usuarios-page">
        <div className="configurar-cargando">
          Cargando usuarios...
        </div>
      </div>
    );

  }


  return (
    <div className="configurar-usuarios-page">

      <div className="configurar-usuarios-container">

        <div className="configurar-usuarios-header">

          <div>
            <h1>
              Administrar Usuarios
            </h1>

            <p>
              Administradores, supervisores e independientes
            </p>
          </div>

        </div>


        {/* BUSCADOR */}

        <div className="configurar-buscador">

          <span className="configurar-buscador-icono">
            🔎
          </span>

          <input
            type="text"
            value={busqueda}
            onChange={(e) =>
              setBusqueda(e.target.value)
            }
            placeholder="Buscar por nombre, usuario, cédula, celular o rol..."
          />

          {busqueda && (
            <button
              type="button"
              className="configurar-buscador-limpiar"
              onClick={() => setBusqueda("")}
            >
              ×
            </button>
          )}

        </div>


        {/* ERROR */}

        {error && (

          <div className="configurar-error">
            {error}
          </div>

        )}


        {/* CONTADOR */}

        <div className="configurar-contador">

          Mostrando{" "}
          <strong>
            {usuariosFiltrados.length}
          </strong>{" "}
          de{" "}
          <strong>
            {usuarios.length}
          </strong>{" "}
          usuarios

        </div>


        {/* LISTA */}

        <div className="configurar-lista">

          {usuariosFiltrados.length === 0 ? (

            <div className="configurar-vacio">

              <div className="configurar-vacio-icono">
                🔍
              </div>

              <h3>
                No se encontraron usuarios
              </h3>

              <p>
                Prueba con otro nombre, usuario,
                cédula o rol.
              </p>

            </div>

          ) : (

            usuariosFiltrados.map((usuario) => {

              const nombre =
                nombreCompleto(usuario);

              const rol =
                obtenerRol(usuario);

              const cantidadCobradores =
                Array.isArray(usuario.cobradores)
                  ? usuario.cobradores.length
                  : 0;

              return (

                <button
                  type="button"
                  className="configurar-usuario-card"
                  key={usuario.id_usuario}
                  onClick={() =>
                    abrirUsuario(usuario)
                  }
                >

                  <div className="configurar-avatar">

                    {nombre
                      ? nombre
                          .charAt(0)
                          .toUpperCase()
                      : "U"}

                  </div>


                  <div className="configurar-usuario-info">

                    <div className="configurar-usuario-nombre">

                      {nombre ||
                        usuario.usuario ||
                        "Sin nombre"}

                    </div>

                    <div className="configurar-usuario-usuario">

                      @{usuario.usuario}

                    </div>


                    <div className="configurar-usuario-detalles">

                      <span
                        className={
                          `configurar-rol configurar-rol-${Number(
                            usuario.id_rol
                          )}`
                        }
                      >
                        {rol}
                      </span>


                      <span
                        className={
                          Number(usuario.activo) === 1
                            ? "configurar-activo"
                            : "configurar-inactivo"
                        }
                      >
                        {Number(usuario.activo) === 1
                          ? "Activo"
                          : "Inactivo"}
                      </span>


                      {Number(usuario.id_rol) === 2 &&
                        cantidadCobradores > 0 && (

                          <span className="configurar-cobradores-badge">
                            👥 {cantidadCobradores} cobrador
                            {cantidadCobradores !== 1
                              ? "es"
                              : ""}
                          </span>

                        )}

                    </div>

                  </div>


                  <div className="configurar-flecha">
                    →
                  </div>

                </button>

              );

            })

          )}

        </div>

      </div>

    </div>
  );
}